import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Smile, ShieldCheck, Heart, Calendar } from 'lucide-react';
const ACCENT = '#5A6B3F'; // muted olive — matches the footer family
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function About() {
  return <div className="w-full min-h-screen bg-[#F5F5F0]" data-id="element-1274">
      {/* HERO */}
      <section className="pt-32 md:pt-36 pb-16 md:pb-24 px-6 md:px-10" data-id="element-1275">
        <div className="max-w-7xl mx-auto" data-id="element-1276">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-1277">
            {/* Left: Copy */}
            <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-xl" data-id="element-1278">
              <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
              color: ACCENT
            }} data-id="element-1279">
                About Us
              </motion.p>

              <motion.h1 variants={fadeUp} className="font-serif text-[#1F1F1F] leading-[1.05] mb-2 text-5xl md:text-6xl" data-id="element-1280">
                Rooted in Science.
              </motion.h1>
              <motion.h1 variants={fadeUp} className="font-serif italic leading-[1.05] mb-7 text-5xl md:text-6xl" style={{
              color: ACCENT
            }} data-id="element-1281">
                Focused on You.
              </motion.h1>

              <motion.div variants={fadeUp} className="w-14 h-px bg-[#1F1F1F]/30 mb-8" data-id="element-1282" />

              <motion.p variants={fadeUp} className="text-base text-[#3A3A33] leading-relaxed mb-5" data-id="element-1283">
                At Structured Nutrition, we believe real results come from
                personalized care, evidence-based strategies, and a coach who
                actually listens. Every plan starts with you — your goals, your
                routine, your medication, your life.
              </motion.p>
              <motion.p variants={fadeUp} className="text-base text-[#3A3A33] leading-relaxed" data-id="element-1284">
                Our mission is simple: help you build healthier habits, improve
                your well-being, and feel your best on GLP-1s — and long after
                you taper off.
              </motion.p>

              {/* CTAs */}
              <motion.div variants={fadeUp} className="mt-10 flex flex-col sm:flex-row gap-4" data-id="element-1285">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-tealslate text-white text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#2A3A3F] transition-colors" data-id="element-1286">
                  Book a Consultation <ArrowRight className="w-4 h-4" data-id="element-1287" />
                </Link>
                <Link to="/services" className="inline-flex items-center justify-center px-7 py-4 border border-[#1F1F1F] text-[#1F1F1F] text-[11px] font-bold uppercase tracking-[0.2em] hover:bg-[#1F1F1F] hover:text-white transition-colors" data-id="element-1288">
                  Explore Services
                </Link>
              </motion.div>
            </motion.div>

            {/* Right: Photo */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.9,
            ease: [0.2, 0.65, 0.3, 0.9]
          }} className="relative w-full" data-id="element-1289">
              <div className="aspect-[4/5] w-full overflow-hidden bg-[#E8E4D8]" data-id="element-1290">
                <img src="https://cdn.magicpatterns.com/uploads/fBWp7VcHydeQL4QeV4Xzp2/pasted-image.png" alt="Jack Achenbach, MS, RDN, CPT — founder of Structured Nutrition" className="w-full h-full object-cover" data-id="element-1291" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-[#E4E6D6] border-y border-[#D2D5C0] py-8 md:py-10 px-6" data-id="element-1292">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-y-8 md:gap-y-0" data-id="element-1293">
          {[{
          icon: Smile,
          stat: '500+',
          label: 'Clients Helped'
        }, {
          icon: ShieldCheck,
          stat: 'Evidence-Based',
          label: 'Nutrition Guidance'
        }, {
          icon: Heart,
          stat: '100%',
          label: 'Personalized Care'
        }, {
          icon: Calendar,
          stat: 'Years of Experience',
          label: 'You Can Trust'
        }].map((item, i) => <div key={i} className={`flex items-center justify-center gap-4 px-4 ${i > 0 ? 'md:border-l md:border-[#C8CBB5]' : ''}`} data-id="element-1294">
              <item.icon className="w-9 h-9 text-[#3D4A2F] shrink-0" strokeWidth={1.3} data-id="element-1295" />
              <div className="text-left" data-id="element-1296">
                <p className="text-base md:text-lg font-serif text-[#1F1F1F] leading-tight" data-id="element-1297">
                  {item.stat}
                </p>
                <p className="text-xs md:text-sm text-[#5A5A50] leading-tight mt-0.5" data-id="element-1298">
                  {item.label}
                </p>
              </div>
            </div>)}
        </div>
      </section>

      {/* OUR STORY */}
      <section className="bg-[#F5F5F0]" data-id="element-1299">
        <div className="max-w-7xl mx-auto" data-id="element-1300">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-1301">
            {/* Left: Food bowl photo */}
            <motion.div initial={{
            opacity: 0,
            x: -24
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: 0.8,
            ease: [0.2, 0.65, 0.3, 0.9]
          }} className="aspect-[2/1] w-full overflow-hidden bg-[#E8E4D8]" data-id="element-1302">
              <img src="https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&w=1200&q=80" alt="Colorful nutrition bowl with grains, avocado, sweet potato, and greens" className="w-full h-full object-cover" data-id="element-1303" />
            </motion.div>

            {/* Right: Copy */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.3
          }} variants={stagger} className="max-w-xl" data-id="element-1304">
              <motion.p variants={fadeUp} className="text-[11px] font-bold uppercase tracking-[0.3em] mb-6" style={{
              color: ACCENT
            }} data-id="element-1305">
                Our Story
              </motion.p>
              <motion.h2 variants={fadeUp} className="font-serif text-[#1F1F1F] leading-[1.15] text-2xl md:text-3xl mb-5" data-id="element-1306">
                A passion for people. A commitment to results.
              </motion.h2>
              <motion.p variants={fadeUp} className="text-sm text-[#3A3A33] leading-relaxed mb-4" data-id="element-1307">
                Structured Nutrition was founded with one goal in mind: to make
                high-quality GLP-1 nutrition care accessible, personalized, and
                effective. We've helped hundreds of clients overcome challenges,
                build confidence, and create sustainable habits that last well
                beyond the medication.
              </motion.p>
              <motion.p variants={fadeUp} className="text-sm text-[#1F1F1F] font-bold leading-relaxed mb-7" data-id="element-1308">
                Your health journey is unique — your plan should be too.
              </motion.p>
              <motion.div variants={fadeUp} data-id="element-1309">
                <Link to="/contact" className="inline-flex items-center gap-2 px-7 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-white transition-colors" style={{
                backgroundColor: ACCENT
              }} data-id="element-1310">
                  Learn More About Me <ArrowRight className="w-4 h-4" data-id="element-1311" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}